import type { ApolloClient } from '@apollo/client'
import type { ViewQueryField, ViewQueryList, ViewQueryNode, ViewQueryNodeReadJoinOnId } from '@salescore/core'
import { message } from 'antd'
import { Array, Effect, Order, pipe, Record, Result } from 'effect'
import { t } from 'i18next'

import { searchRelation } from '../../recoil/hooks/searchRelation'
import { fetchChildRecordNode } from '../../recoil/records/mutations/fetchChildNode'
import { getParentReferenceField } from '../../recoil/records/mutations/getParentReferenceField'
import type { MagnifyingGlassChange } from '../useUpsertViewRecordsEntry/applyMagnifyingGlassChange'
import type { ChangeCandidate } from './generateChangeCandidates'

// eslint-disable-next-line sonarjs/todo-tag
// TODO: client を domain ディレクトリ以下で使いたくない
export const generateMagnifyingGlassChanges = async (
  changeCandidates: ChangeCandidate[],
  query: Pick<ViewQueryList, 'tree' | 'fields'> | undefined,
  organizationId: string,
  client: ApolloClient<object>,
): Promise<{
  magnifyingGlassChanges: MagnifyingGlassChange[]
  errors: Array<{ message: string }>
}> => {
  if (query === undefined) {
    return { magnifyingGlassChanges: [], errors: [] }
  }
  const groupedChanges = groupMagnifyingGlassChanges(changeCandidates, query)
  if (groupedChanges.length > 3) {
    void message.warning(
      t(`更新するレコード数が多い場合や、参照列へのペーストには時間がかかります。完了後に保存してください。`),
    )
  }

  const results = await Effect.runPromise(
    Effect.all(
      groupedChanges.map((group) =>
        Effect.tryPromise({
          try: async () => {
            const [{ searchSql, value, column, parentReferenceField }] = group
            // searchKey はラベルであり、それからSFA上でのレコードIDを検索する
            const searchResult = await searchRelation(client, organizationId)(
              searchSql,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              value as string,
              undefined,
              undefined,
            )
            const results = searchResult?.values ?? []
            const [bestMatch] = pipe(results, Array.sortBy(Order.mapInput(Order.Number, (x) => x.label.length))) // LIKE検索しているため、複数のレコードがヒットしうる。文字数が最小のものが最適と仮定して、これを選択する
            if (bestMatch === undefined) {
              throw new Error(t(`{{searchKey}}は見つかりませんでした`, { searchKey: value }))
            }

            // SFA上のレコードIDから、RecordNodeを取得する
            const fetchedChildRecordNode = await fetchChildRecordNode(
              column.node,
              bestMatch.value,
              query,
              client,
              parentReferenceField,
            )

            if (fetchedChildRecordNode?.id === undefined) {
              throw new Error(t(`レコードが見つかりませんでした`))
            }

            return group.map(({ rowIndex, innerRowIndex, column, parentReferenceField, parentNode, joinOnId }) => ({
              innerRowIndex,
              joinOnId,
              rowIndex,
              node: column.node,
              parentNode,
              parentReferenceField,
              fetchedChildNode: fetchedChildRecordNode,
            }))
          },
          catch: (error) => ({ message: String(error) }),
        }),
      ),
      {
        mode: 'result',
        concurrency: 3,
      },
    ),
  )
  const [successes, errors] = Array.separate(results)
  const magnifyingGlassChanges = successes.flat()
  return {
    magnifyingGlassChanges,
    errors,
  }
}

export const groupMagnifyingGlassChanges = (
  changeCandidates: ChangeCandidate[],
  query: Pick<ViewQueryList, 'tree' | 'fields'>,
): Array<
  Array.NonEmptyArray<
    ChangeCandidate & {
      searchSql: string
      parentReferenceField: ViewQueryField | undefined
      parentNode: ViewQueryNode
      joinOnId: ViewQueryNodeReadJoinOnId
    }
  >
> =>
  pipe(
    changeCandidates,
    Array.filterMap((candidate) => {
      const getParentEither = getParentReferenceField(query, candidate.column.node)
      if (Result.isFailure(getParentEither)) {
        return Result.failVoid
      }
      const getParentResult = getParentEither.success

      return Result.succeed({
        ...candidate,
        searchSql: getParentResult.join.search.sql,
        parentReferenceField: getParentResult.parentReferenceField,
        parentNode: getParentResult.parentNode,
        joinOnId: getParentResult.join.joinOn,
      })
    }),
    Array.groupBy((x) => `${x.searchSql}_${String(x.value)}`),
    Record.values,
  )
